import { InstallationType } from "components/EtudeForm/hooks/useGetSolutionsQuery";
import { ServiceNames } from "components/EtudeForm/Solutions/Solutions";
import { ProjectTrace } from "types/ProjectTrace";
import { roundTo1Decimals } from "utils";
import { SystemCode } from "utils/engine/consts";
import { boilerTypeArrayToApiNumber } from "utils/helpers/boilerType";
import { transformAppoint, transformCodeGammePac } from "./project";
import { getSolutionNomDePropositionFromPanier } from "components/EtudeForm/Solutions/SolutionDetails/helpers";

function isValid(value: any): boolean {
  return value !== null && value !== undefined && value !== "";
}

const extractAccessories = (source: any, result: any) => {
  if (!source) return;
  Object.entries(source).forEach(([code, value]) => {
    result.push({
      QuoteAcc: code,
      QuoteQuantAcc: value === true ? 1 : value === false ? 0 : value,
    });
  });
};

const extractQuotes = (source: any, result: any) => {
  if (!source) return;
  source.forEach((q: any) => {
    result.push({
      QuoteEquipment: q.code,
      QuoteQuantFourniture: q.quantite,
    });
  });
};

type MappingConfig<T> = {
  [K in keyof T]?: string | ((values: any) => T[K]);
};

function mapFields<T>(values: any, config: MappingConfig<T>): Partial<T> {
  const result: Partial<T> = {};

  for (const key in config) {
    const resolver = config[key];

    let value: any;

    if (typeof resolver === "function") {
      value = resolver(values);
    } else if (typeof resolver === "string") {
      value = values[resolver];
    }

    if (isValid(value)) {
      result[key] = value;
    }
  }

  return result;
}

export default function MapValuesToProjectTrace(
  values: any,
  t: any
): ProjectTrace {
  const mappedFields = mapFields<ProjectTrace>(values, {
    id: "Id",
    salesNetwork: "network",
    inputService: "service",
    inputType: "projectType",
    inputInstallation: "installationType",
    inputAC: (v) => v.antiCorrosion === "yes" || v.antiCorrosion === true,
    inputCycleAL: (v) =>
      v.antiLegionellaSupport === "yes" || v.antiCorrosion === true,
    inputBdm: (v) => v.bdm === "yes" || v.bdm === true,
    internal_user: "internal_user",
    inputDepartment: "dep_code",
    inputCity: "city",
    inputAltitude: "altitude",
    inputTBase: "t_base",
    inputTcoupure: "Tcoupure",
    inputTEte: "t_ete",
    inputHeatStart: (v) => v.service != "ecs" ? v.debut_nc : undefined,
    inputHeatEnd: (v) => v.service != "ecs" ? v.fin_nc : undefined,
    inputDist_HP_Tank: "distance_pac_ballon",
    inputDist_HP_Batiment: "distance_pac_batiment",
    inputDWHbyBoiler: (v) =>
      v.boilerForEcs === "yes" || v.boilerForEcs === true,
    inputDWHapplication: "application",
    inputDWHSubapplication: "subApplication",
    circuits: "circuits",
    inputTEFS: "TEFS",
    inputNbT1: "nbT1",
    inputNbT2: "nbT2",
    inputNbT3: "nbT3",
    inputNbT4: "nbT4",
    inputNbT5: "nbT5",
    inputNbT6: "nbT6",
    inputNbBed: "NbLit",
    inputNbStar: "NbE",
    inputNbBedroom: "NbC",
    inputNbLunchPlaces: "NbCouvM",
    inputNbDinnerPlaces: "NbCouvS",
    inputNbShowers: "NbD",
    inputNbPeopleMatch: "PersActiv",
    inputNbMatchByDay: "NbMPJ",
    inputNbPeopleSim: "personnes",
    inputNbActByDay: "NbActiv",
    inputStartActivity: "TpsActSport",
    inputDailyTraffic: "freq",
    inputConsoT1: "consoT1",
    inputConsoT2: "consoT2",
    inputConsoT3: "consoT3",
    inputConsoT4: "consoT4",
    inputConsoT5: "consoT5",
    inputConsoT6: "consoT6",
    inputConsoBuilding: "consoBatiment",
    inputDailyVolumeBed: "Cjpl",
    inputDailyConsoBedroom: "Cjpc",
    inputConsoByPlace: "ECSbyCouv",
    inputRestPeakTime: "tRestau",
    inputSim: (v) => (v == "MIDI" ? "NOON" : "EVENING"),
    inputShowerFlow: "QDch",
    inputShowerTime: "TpsEcDch",
    inputShowerOccTime: "TpsTotDch",
    inputPoolOpen: "NbPuisages",
    inputPeakPeriod: "TpsAffl",
    inputTrafficPeak: "Affl",
    inputTrafficOffPeak: "NAffl",
    DHWNeedsPeak10: (v) => Math.round(+v.v10),
    DHWNeedsMultiHourPeakTime: "tph",
    DHWNeedsDailyVolume: (v) => Math.round(+v.vj),
    DHWNeedsMultiHourPeakVolume: (v) => Math.round(+v.vph),
    inputDWHSysteme: "systeme",
    inputHeatLoss: "Pboucle",
    inputPrimaryTemp: "temperature_primaire",
    inputStorageTemp: "TECS",
    inputSetpointAL: "antiLegionella",
    inputWidth: "largeur_porte",
    inputHeight: "hauteur_porte",
    inputDHWTankJacket: (v) => v.Jaquette?.text,
    inputDHWTankOpening: (v) => v.Ouverture?.text,
    inputDHWNbTank: (v) => {
      if (typeof v.nbBallon == "object") return undefined;
      return v.nbBallon;
    },
    inputExchangeCircPrim: (v) =>
      v.circPrim == 1 ? t("FO_INFO_SIMPLE") : t("FO_INFO_DOUBLE"),
    inputExchangeCircSec: (v) =>
      v.circSec == 1 ? t("FO_INFO_SIMPLE") : t("FO_INFO_DOUBLE"),
    inputDelay: (v) => {
      switch (v.inputDelay) {
        case "1": return t("FO_HP_ORDER_DATE_1_3");
        case "2": return t("FO_HP_ORDER_DATE_3_6");
        case "3": return t("FO_HP_ORDER_DATE_6_12");
        case "4": return t("FO_HP_ORDER_DATE_12_18");
        case "5": return t("FO_HP_ORDER_DATE_SUPP_18");
        default: "";
      }
    },

    version: (v) => v.etudeData?.version,
    surveyNumber: (v) => v.etudeData?.numero,
    adminSave: (v) => v.etudeData?.saved,
    adminDownload: (v) => v.downloaded,
    adminSiteRef: (v) => v.etudeData?.reference,
    adminSiteName: (v) => v.etudeData?.siteName,
    adminSiteAdress: (v) => v.etudeData?.siteAdress,
    adminSiteCP: (v) => v.etudeData?.siteCP,
    adminSiteCity: (v) => v.etudeData?.siteCity,
    adminClientName: (v) => v.etudeData?.clientName,
    adminClientContact: (v) => v.etudeData?.clientContact,
    adminClientAdress: (v) => v.etudeData?.clientAdress,
    adminClientCP: (v) => v.etudeData?.clientCP,
    adminClientCity: (v) => v.etudeData?.clientCity,
    adminClientEmail: (v) => v.etudeData?.clientMail,
    adminClientPhone: (v) => v.etudeData?.clientPhoneNumber,
    filterCoverageRate: (v) => v.solutionRequestBody?.filtres?.taux_couverture,
    filterHPportionTbase: (v) => v.solutionRequestBody?.filtres?.part_pac,
    filterHPelectricalAuxiliary: (v) =>
      transformAppoint(v.solutionRequestBody?.filtres?.appoint),
    filterHPobjective: (v) => v.solutionRequestBody?.filtres?.objectif,
    filterHPfluidType: (v) =>
      transformCodeGammePac(v.solutionRequestBody?.filtres?.code_gamme_pac),
    filterHPnumberHP: (v) => v.solutionRequestBody?.filtres?.nombre_de_pac,
    filterHPmodelHP: (v) =>
      JSON.stringify(v.solutionRequestBody?.filtres?.preference_pac),
    filterHPacoustic: (v) => v.solutionRequestBody?.filtres?.charge_acoustique,
    filterHPAcousticComfort: (v) => v.solutionRequestBody?.filtres?.confort_acoustique,
    filterHPelectricalTarif: (v) =>
      v.solutionRequestBody?.filtres?.abonnement_elec,
    filterHPceilingHeight: (v) => v.solutionRequestBody?.filtres?.hauteur_porte,
    filterHPpassageWidth: (v) => v.solutionRequestBody?.filtres?.largeur_porte,
    filterBoilerType: (v) => {
      const mapped = boilerTypeArrayToApiNumber(
        v.solutionRequestBody?.filtres?.type_chaudiere
      );

      return typeof mapped === "number" ? mapped : undefined;
    },
    outputSolution: (v) => {
      if (v.noSolution) return "NO SOLUTION";
      return getSolutionNomDePropositionFromPanier(v.panier);
    },
    outputTotalAux: (v) =>
      v.ballons?.primaires?.reduce(
        (partialSum: number, p: any) =>
          partialSum +
          p?.appoints.reduce(
            (sum: any, app: any) => sum + app.puissance || 0,
            0
          ) || 0,
        0
      ),
    outputTank1: (v) =>
      v.installationType === InstallationType.electric
        ? v.ballons?.primaires && v.ballons?.primaires[0]?.nom ? v.ballons?.primaires[0]?.code == "FXBALLON" ? "FO_SOL_FAUX_BALLON" : v.ballons?.primaires[0]?.nom : ""
        : v.ballons && v.ballons[0]?.nom,
    outputAuxiliaryTank1: (v) => {
      let ballons =
        v.installationType === InstallationType.electric
          ? v.ballons?.primaires
          : v.ballons;
      const ballon = ballons?.[0];

      if (ballon?.code === "FXBALLON") {
        return "FO_SOL_FAUX_BALLON";
      }

      return (
        ballon?.appoints
          ?.map((a: any) => a.puissance + ` ${t("FO_UNIT_KW")}`)
          .join(" + ")
      );
    },
    outputTank2: (v) =>
      v.installationType === InstallationType.electric
        ? v.ballons?.primaires && v.ballons?.primaires[1]?.nom ? v.ballons?.primaires[1]?.code == "FXBALLON" ? "FO_SOL_FAUX_BALLON" : v.ballons?.primaires[1]?.nom : ""
        : v.ballons && v.ballons[1]?.nom,
    outputSuppTank: (v) => {
      const ballon =
        v.service === ServiceNames.ECS
          ? v.ballons?.primaires?.[2]
          : v.ballons?.secondaires?.[0];

      if (ballon?.code === "FXBALLON") {
        return "FO_SOL_FAUX_BALLON";
      }

      return ballon?.nom;
    },

    outputAuxiliarySuppTank: (v) => {
      const ballon =
        v.service === ServiceNames.ECS
          ? v.ballons?.primaires?.[2]
          : v.ballons?.secondaires?.[0];

      if (ballon?.code === "FXBALLON") {
        return "FO_SOL_FAUX_BALLON";
      }

      return ballon?.appoints
        ?.map((a: any) => a.puissance + ` ${t("FO_UNIT_KW")}`)
        .join(" + ");
    },
    outputExchange: (v) => {
      return v.systeme === SystemCode.BALLONS_ECS_PREPARATEURS
        ? undefined
        : v.ecs?.echangeur?.nom;
    },

    outputPrepTank: (v) => {
      return v.systeme === SystemCode.BALLONS_ECS_PREPARATEURS
        ? v.ecs?.echangeur?.nom
        : undefined;
    },

    outputStockTank1: (v) => v.ecs?.ballons_stockage_ecs?.[0]?.nom,

    outputStockTank2: (v) => v.ecs?.ballons_stockage_ecs?.[1]?.nom,

    outputCircuit: (v) => {
      const circuits = v.circuits_hybrides;
      if (!circuits || circuits.length === 0) return "";

      return `${t(
        circuits.length > 1 ? "FO_RESULT_ALL_CIRCUITS" : "FO_RESULT_CIRCUIT1"
      )} ${circuits.length > 1 ? "" : circuits.join(" ")}`;
    },
    outputHPportionHeating: (v) => {
      const hasTcoupure = !!(v?.TcoupureQuestion && v?.Tcoupure != null);
      const partPac = hasTcoupure
        ? v.part_pac_t_coupure ?? v.part_pac
        : v.part_pac;

      return Math.round(partPac * 100);
    },
    outputHPportionHeatingDWH: (v) => {
      const hasTcoupure = !!(v?.TcoupureQuestion && v?.Tcoupure != null);
      const dsPartPac = hasTcoupure
        ? v.ecs?.ds_part_pac_a_t_coupure ?? v.ecs?.ds_part_pac_a_t_base
        : v.ecs?.ds_part_pac_a_t_base;

      return dsPartPac != null ? Math.round(dsPartPac * 100) : undefined;
    },
    outputHPPower: (v) => {
      const hasTcoupure = !!(v?.TcoupureQuestion && v?.Tcoupure != null);
      const hpPower = hasTcoupure
        ? v.pac?.puissance_a_t_coupure ?? v.pac?.puissance_a_t_base ?? v.puissance_a_t_base
        : v.pac?.puissance_a_t_base ?? v.puissance_a_t_base;

      return hpPower != null ? roundTo1Decimals(hpPower) : undefined;
    },
    outputAnnualHPportion: (v) => Math.round(v.taux_couverture * 100),
    outputElectricalPower: (v) => roundTo1Decimals(v.puissance_elec_max),
    outputElectricalIntensity: (v) => roundTo1Decimals(v.intensite_elec_max),
    outputApparentPower: (v) => roundTo1Decimals(v.puissance_active),
    outputHPflow: (v) => roundTo1Decimals(v.debit_primaire_pac_total),
    outputVolume: (v) => roundTo1Decimals(v.pac?.volume_primaire),
    outputPipeDiameter: (v) =>
      v.diametre_raccordement_collecteur?.replace("_", '"'),
    outputHMT: (v) => +v.hmt_disponible?.toFixed(2),
    outputMaxPressure: (v) => v.pac?.pression_max,
    outputCoilFlow: (v) => roundTo1Decimals(v.ecs?.debit_primaire_preconise),
    outputInstallationArea: (v) =>
      roundTo1Decimals(v.ecs?.surface_sol_degagement),
    outputHPsize: (v) => {
      const pac = v.pac;
      if (!pac) return;

      return `${pac.hauteur} x ${pac.largeur} x ${pac.profondeur} ${t(
        "FO_UNIT_MM"
      )}`;
    },
    outputCeillingHeight: (v) => v.gros_ballon?.hauteur,
    outputPassageWidth: (v) => v.gros_ballon?.diametre,
    outputAcousticPower: (v) => v.puissance_sonore?.charge_partielle,
    quoteEquipments: (v) => {
      const result: any = [];
      extractQuotes(v?.panier, result);

      return result;
    },
    outputSchema: (v) => v.code_schema?.pdf || v.code_schema?.dwg,
    quoteAccs: (v) => {
      const result: any = [];

      extractAccessories(v?.accessoires?.selectedAccessoiresNonSupp, result);
      extractAccessories(v?.accessoires?.selectedAccessoiresSupp, result);
      extractAccessories(v?.accessoires?.selectedBoilers, result);
      extractAccessories(v?.accessoires?.selectedServices, result);

      return result;
    },
  });

  return mappedFields as ProjectTrace;
}
